/**
 * Cut a printed sheet into real pages, each carrying the full frame.
 *
 * The prescription slip is one tall flex column: letterhead, patient bar, two-column body, tail,
 * footer. When it outgrew A4 the browser spilled the overflow onto a second sheet that had no
 * letterhead, no patient bar and no footer, because there is only ever one of each and they sit at
 * the two ends of the column. A page of unlabelled medicines is a real hazard: handed over on its
 * own, nothing on it says whose prescription it is.
 *
 * CSS cannot fix this. Repeating content per page needs either paged-media margin boxes, which no
 * browser implements, or a table's thead/tfoot, which repeats the SAME element and so cannot carry
 * a per-page number. So the sheet is cut up explicitly.
 *
 * The markup says which part is which, so this file never guesses at the slip's structure:
 *
 *   data-rx-header    letterhead                         repeated on every page
 *   data-rx-patient   patient bar                        repeated on every page
 *   data-rx-body      the two-column row                 frame repeated on every page
 *   data-rx-sidebar   left column, the clinical picture  content on page 1 only
 *   data-rx-flow      right column                       split across pages
 *   data-rx-tail      instructions + follow-up           split across pages, after the flow
 *   data-rx-footer    the footer strip                   repeated on every page
 *   data-rx-pageno    empty span inside the footer       filled with "Page 2 of 3"
 *   data-rx-watermark the faint logo behind the content  repeated on every page (optional)
 *
 * The owner chose, 2026-09-20: keep the divider on continuation pages and leave the sidebar empty,
 * so the medicines stay in the same column at the same width on every sheet.
 *
 * ## How it decides where to cut
 *
 * Two passes, and the order matters.
 *
 * FIRST the sheet is measured and reduced to a flat list of ATOMS: the largest pieces that still
 * fit a page on their own. A whole medicine card is an atom, so a card is never sliced through the
 * middle; only something genuinely taller than a page is opened up and its parts taken instead.
 * The flow's children are NOT atoms by default, because the medicines are not siblings of the
 * "Medications" heading: they live inside a flex wrapper that carries the gap between cards, and
 * the lab tests are a level deeper again.
 *
 * THEN the atoms are poured into pages in order, each rebuilding whatever wrapper chain it needs
 * so the flex gaps, rules and margins survive the cut.
 *
 * Splitting the two passes is what makes the result trustworthy: the atom list is fixed before any
 * page exists, and the pour appends every atom exactly once, so nothing can quietly disappear. An
 * earlier version decided and placed in one recursive pass and silently dropped whichever medicine
 * happened to straddle the page break. It passed its unit tests, because their cards were leaf
 * nodes and so never took the path that lost one; a real 18-medicine prescription printed 1-12 and
 * then 14-16.
 */

/** CSS pixels per millimetre. Fixed at 96dpi, which is what a browser resolves `mm` against. */
const PX_PER_MM = 96 / 25.4;

/** A4. The sheet carries its own padding, so nothing is taken off here. 296 not 297, see
 * PrescriptionPrint: at exactly one page height, rounding spills a blank second sheet. */
export const SHEET_HEIGHT_MM = 296;

/**
 * Replace `root` with one element per printed page. Returns the number of pages, or 0 when the
 * sheet is not one this understands, in which case nothing is touched and the caller prints the
 * single long sheet exactly as before.
 *
 * Must run with `root` laid out in a real document: every decision here is a measurement.
 */
export function paginatePrintSheet(root, { heightMm = SHEET_HEIGHT_MM } = {}) {
  if (!root || !root.ownerDocument || !root.parentNode) return 0;

  const header = root.querySelector('[data-rx-header]');
  const patient = root.querySelector('[data-rx-patient]');
  const body = root.querySelector('[data-rx-body]');
  const flow = root.querySelector('[data-rx-flow]');
  const footer = root.querySelector('[data-rx-footer]');
  // Without all of these there is no frame to repeat, so leave the sheet alone rather than emit
  // something half-built.
  if (!header || !patient || !body || !flow || !footer) return 0;

  const doc = root.ownerDocument;
  const sidebar = root.querySelector('[data-rx-sidebar]');
  const tail = root.querySelector('[data-rx-tail]');
  const watermark = root.querySelector('[data-rx-watermark]');
  const limit = heightMm * PX_PER_MM;

  // The slip carries its own <style> as a child of the root. Dropping the root would drop those
  // rules with it, so move them to the document first. They are the @media print geometry and the
  // Urdu @font-face, and both then apply to every page rather than to one.
  Array.from(root.children).forEach((child) => {
    if (child.tagName === 'STYLE') (doc.head || doc.body).appendChild(child);
  });

  const pages = [];
  const holder = doc.createElement('div');
  root.parentNode.insertBefore(holder, root);

  let page = null;
  let slots = null;
  let shells = null; // source element -> its shell on the CURRENT page

  const buildPage = (withSidebar) => {
    // The page IS the sheet: same element, same class, same inline styles, so every rule the slip
    // sets for itself (width, padding, base font, the flex column that pins the footer) comes
    // along. Only the height is taken over, and only once every page is full.
    const el = root.cloneNode(false);
    el.style.minHeight = '0';
    el.style.height = 'auto';

    // Absolutely positioned against the page, so it adds no height and every page gets its own.
    if (watermark) el.appendChild(watermark.cloneNode(true));
    el.appendChild(header.cloneNode(true));
    el.appendChild(patient.cloneNode(true));

    const bodyEl = body.cloneNode(false);
    if (sidebar) {
      // Deep on page 1, shallow after: an empty column of the same width keeps the divider and the
      // medicines' column position identical on every sheet.
      bodyEl.appendChild(sidebar.cloneNode(withSidebar));
    }
    const flowEl = flow.cloneNode(false);
    bodyEl.appendChild(flowEl);
    el.appendChild(bodyEl);

    let tailEl = null;
    if (tail) {
      tailEl = tail.cloneNode(false);
      // Hidden until something lands in it, so a page with an empty tail does not draw the tail's
      // top rule across a blank strip.
      tailEl.style.display = 'none';
      el.appendChild(tailEl);
    }

    el.appendChild(footer.cloneNode(true));
    return { el, flow: flowEl, tail: tailEl };
  };

  const startPage = () => {
    const built = buildPage(pages.length === 0);
    holder.appendChild(built.el);
    pages.push(built.el);
    page = built.el;
    slots = { flow: built.flow, tail: built.tail };
    shells = new Map();
  };

  // How much room an atom can ever have: a CONTINUATION page's frame, which is the roomiest,
  // because its sidebar is empty. Measured rather than worked out, so a longer letterhead or a
  // two-line footer is accounted for on its own.
  const probe = buildPage(false);
  holder.appendChild(probe.el);
  const budget = limit - probe.el.getBoundingClientRect().height;
  holder.removeChild(probe.el);

  // ---- pass one: the atoms, largest pieces that still fit a page on their own ----------------
  const atoms = [];
  const collect = (node) => {
    const fits = node.getBoundingClientRect().height <= budget;
    if (fits || node.childElementCount === 0) {
      // Either it fits whole, or there is nothing left to open up and it is simply taller than A4.
      // Keeping an oversized atom and letting its sheet run long beats dropping a medicine.
      atoms.push(node);
      return;
    }
    Array.from(node.children).forEach(collect);
  };
  Array.from(flow.children).forEach((child) => collect(child));
  const flowAtomCount = atoms.length;
  if (tail) Array.from(tail.children).forEach((child) => collect(child));

  if (atoms.length === 0) return 0;

  // ---- pass two: pour them into pages ---------------------------------------------------------
  /**
   * The container on the current page that `atom` belongs in, creating whatever wrappers are
   * missing. Wrappers are shared with the atom before it when they have the same source, so a run
   * of medicines sits inside ONE copy of the list and keeps the gap between cards.
   */
  const containerFor = (atom, region, regionSource) => {
    const chain = [];
    for (let p = atom.parentElement; p && p !== regionSource; p = p.parentElement) chain.unshift(p);
    let target = region;
    chain.forEach((source) => {
      let shell = shells.get(source);
      if (!shell || !target.contains(shell)) {
        shell = source.cloneNode(false);
        target.appendChild(shell);
        shells.set(source, shell);
      }
      target = shell;
    });
    return target;
  };

  startPage();
  let placedOnPage = 0;

  atoms.forEach((atom, i) => {
    const inTail = i >= flowAtomCount;
    const which = inTail ? 'tail' : 'flow';
    if (inTail && !slots.tail) return;

    const show = () => {
      if (slots.tail && which === 'tail') slots.tail.style.display = '';
    };

    show();
    let target = containerFor(atom, slots[which], inTail ? tail : flow);
    const copy = atom.cloneNode(true);
    target.appendChild(copy);

    if (page.getBoundingClientRect().height > limit && placedOnPage > 0) {
      // It does not fit what is left of this page, and the page is not empty, so it starts the
      // next one. Removed and re-appended, never dropped.
      copy.remove();
      pruneEmpty(slots.flow);
      if (slots.tail) pruneEmpty(slots.tail);
      startPage();
      placedOnPage = 0;
      show();
      target = containerFor(atom, slots[which], inTail ? tail : flow);
      target.appendChild(copy);
    }
    placedOnPage += 1;
  });

  // A page that never received a tail atom must not draw the tail's rule across a blank strip.
  pages.forEach((el) => {
    pruneEmpty(el.querySelector('[data-rx-flow]'));
    const t = el.querySelector('[data-rx-tail]');
    if (!t) return;
    pruneEmpty(t);
    if (t.childElementCount === 0) t.style.display = 'none';
  });

  // Now fix every page to a full sheet, which is what pins each footer to the bottom, and break
  // after all but the last.
  pages.forEach((el, i) => {
    el.style.minHeight = `${heightMm}mm`;
    el.style.height = `${heightMm}mm`;
    el.style.breakAfter = i < pages.length - 1 ? 'page' : 'auto';
    el.style.pageBreakAfter = i < pages.length - 1 ? 'always' : 'auto';
    const slot = el.querySelector('[data-rx-pageno]');
    // Only worth saying when there IS more than one page; "Page 1 of 1" is noise.
    if (slot) slot.textContent = pages.length > 1 ? `Page ${i + 1} of ${pages.length}` : '';
  });

  root.parentNode.removeChild(root);
  // The holder would nest the pages inside a block of its own; unwrap so each page is a sibling,
  // which is what the page-break rules above need.
  const parent = holder.parentNode;
  while (holder.firstChild) parent.insertBefore(holder.firstChild, holder);
  parent.removeChild(holder);

  return pages.length;
}

/** Drop wrapper shells a page break emptied. Depth-first, so a shell of shells goes too. */
function pruneEmpty(region) {
  if (!region) return;
  Array.from(region.children).forEach((child) => {
    pruneEmpty(child);
    if (child.childElementCount === 0 && !child.textContent.trim()) child.remove();
  });
}
